<template>
  <div
    class="play-overlay"
    :class="{ 'play-overlay--wrap': !noWrap, 'play-overlay--bare': noWrap }"
    :aria-busy="show ? 'true' : 'false'"
  >
    <slot v-if="!noWrap" />

    <transition name="play-overlay-fade">
      <div
        v-if="show"
        class="play-overlay__veil"
        :class="[
          `play-overlay__veil--bg-${bg}`,
          `play-overlay__veil--${rounded}`,
          { 'play-overlay__veil--no-center': noCenter },
        ]"
        :style="veilStyle"
      >
        <div
          class="play-overlay__content"
          :class="`play-overlay__content--${align}`"
        >
          <slot name="overlay">
            <PlaySpinner
              :type="spinnerType"
              :variant="spinnerVariant"
              :size="spinnerSize"
            />
          </slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import PlaySpinner from '@/components/play/PlaySpinner.vue'

/**
 * PlayOverlay — busy / loading veil for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-overlay>. Two modes:
 *  - wrap (default): wraps the default slot; a veil + centered <PlaySpinner>
 *    fades in over it while `show` is true (the content stays in the DOM).
 *  - `no-wrap`: renders only the veil (absolute-fill), to drop into a host that
 *    already positions it (e.g. the full-screen booking loader).
 *
 * `bg` picks the backdrop surface role; `opacity`/`blur`/`rounded` tune the
 * look; `z-index` controls stacking; `spinner-*` forward to <PlaySpinner>; the
 * `overlay` slot replaces the spinner with custom content. `aria-busy` is set on
 * the root for assistive tech.
 *
 * VUE3-MIGRATION: transition classes use Vue 2 names (`-enter`/`-leave`); Vue 3
 * renames them to `-enter-from`/`-leave-from` (see the <style> block). Props/
 * slots are already Vue 3-safe.
 */
export default {
  name: 'PlayOverlay',
  components: { PlaySpinner },
  props: {
    /** Toggle the veil */
    show: {
      type: Boolean,
      default: false,
    },
    /** Render only the veil (absolute-fill), without wrapping any content */
    noWrap: {
      type: Boolean,
      default: false,
    },
    /** Backdrop surface role */
    bg: {
      type: String,
      default: 'card',
      validator: v => ['card', 'bg', 'ink', 'transparent'].includes(v),
    },
    /** Backdrop opacity (0–1) */
    opacity: {
      type: [Number, String],
      default: 0.85,
    },
    /** Backdrop blur in px (0 disables) */
    blur: {
      type: [Number, String],
      default: 2,
    },
    /** Corner rounding role */
    rounded: {
      type: String,
      default: 'inherit',
      validator: v => ['none', 'sm', 'md', 'lg', 'xl', 'inherit'].includes(v),
    },
    /** Stacking order */
    zIndex: {
      type: [Number, String],
      default: 10,
    },
    /** Pin the content to the top instead of centering it vertically */
    noCenter: {
      type: Boolean,
      default: false,
    },
    /** Horizontal placement of the overlay content */
    align: {
      type: String,
      default: 'center',
      validator: v => ['start', 'center', 'end'].includes(v),
    },
    /** Forwarded to <PlaySpinner> */
    spinnerType: {
      type: String,
      default: 'border',
    },
    spinnerVariant: {
      type: String,
      default: 'primary',
    },
    spinnerSize: {
      type: [Number, String],
      default: 'md',
    },
  },
  computed: {
    veilStyle() {
      const blur = Number(this.blur)
      return {
        '--ov-opacity': this.opacity,
        '--ov-blur': blur ? `${blur}px` : '0px',
        '--ov-z': this.zIndex,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.play-overlay {
  &--wrap { position: relative; }
  &--bare { position: static; }

  /* ── Backdrop layer (the layer itself is transparent; the ::before paints
       the tinted/blurred surface so the spinner above stays fully opaque) ── */
  &__veil {
    --ov-bg: var(--surface-card);
    position: absolute;
    inset: 0;
    z-index: var(--ov-z, 10);
    display: flex;
    align-items: center;
    padding: 16px;
    background: transparent;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: var(--ov-bg);
      opacity: var(--ov-opacity, .85);
      -webkit-backdrop-filter: blur(var(--ov-blur, 2px));
      backdrop-filter: blur(var(--ov-blur, 2px));
    }

    &--no-center { align-items: flex-start; }
  }

  /* backdrop surface roles */
  &__veil--bg-card        { --ov-bg: var(--surface-card); }
  &__veil--bg-bg          { --ov-bg: var(--surface-bg); }
  &__veil--bg-ink         { --ov-bg: var(--surface-ink); }
  &__veil--bg-transparent { --ov-bg: transparent; }

  /* ── Content holder (sits above the ::before) ── */
  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    width: 100%;

    &--start  { justify-content: flex-start; }
    &--center { justify-content: center; }
    &--end    { justify-content: flex-end; }
  }

  /* ── Corner rounding ── */
  &__veil--none    { border-radius: 0; }
  &__veil--sm      { border-radius: var(--radius-sm); }
  &__veil--md      { border-radius: var(--radius); }
  &__veil--lg      { border-radius: var(--radius-lg); }
  &__veil--xl      { border-radius: var(--radius-xl); }
  &__veil--inherit { border-radius: inherit; }
}

/* ── Transition (Vue 2 class names — see VUE3-MIGRATION) ── */
.play-overlay-fade-enter-active,
.play-overlay-fade-leave-active {
  transition: opacity var(--dur) var(--ease-out);
}

.play-overlay-fade-enter,
.play-overlay-fade-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .play-overlay-fade-enter-active,
  .play-overlay-fade-leave-active { transition: none; }
}
</style>
